// GAP-05 — Poisson score-matrix transparency (OddsJam Wave 1, P1).
// Pure & presentational. Renders the existing scoreline grid (real
// math from src/fair-price/scoreline-grid.ts) as a 5×5 heat matrix
// (home 0–4 × away 0–4). Additive: the drawer keeps its free Top-6
// bars; this is the deeper Pro+ "Model" view. No fabrication — only
// renders cells the engine produced.

import type { ScorelineCell } from "../../fair-price/scoreline-grid.ts";

export interface PoissonScoreMatrixProps {
  readonly cells: readonly ScorelineCell[];
  /** Highest score shown per axis (spec: 5×5 ⇒ 0..4). */
  readonly maxShown?: number;
}

function heat(p: number, pMax: number): string {
  if (pMax <= 0) return "transparent";
  const t = Math.min(1, p / pMax);
  // amber heat ramp; opacity ∝ relative probability.
  return `rgba(212,160,23,${(0.08 + t * 0.62).toFixed(3)})`;
}

export function PoissonScoreMatrix(
  { cells, maxShown = 4 }: PoissonScoreMatrixProps,
): JSX.Element {
  const shown = cells.filter((c) => c.home <= maxShown && c.away <= maxShown);
  const pMax = shown.reduce((m, c) => (c.probability > m ? c.probability : m), 0);
  const at = (h: number, a: number): number =>
    shown.find((c) => c.home === h && c.away === a)?.probability ?? 0;
  const axis = Array.from({ length: maxShown + 1 }, (_, i) => i);

  return (
    <table
      data-testid="poisson-score-matrix"
      style={{
        borderCollapse: "collapse",
        fontFamily: "var(--se-font-mono, monospace)",
        fontSize: 11,
        color: "var(--se-text-sec, #aaa)",
      }}
    >
      <thead>
        <tr>
          <th style={{ padding: 4, color: "var(--se-text-muted, #888)" }}>
            H\A
          </th>
          {axis.map((a) => (
            <th key={`c${a}`} style={{ padding: "4px 6px" }}>{a}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {axis.map((h) => (
          <tr key={`r${h}`}>
            <th style={{ padding: "4px 6px", color: "var(--se-text-muted, #888)" }}>
              {h}
            </th>
            {axis.map((a) => {
              const p = at(h, a);
              const top = p === pMax && pMax > 0;
              return (
                <td
                  key={`${h}-${a}`}
                  data-testid={`psm-${h}-${a}`}
                  style={{
                    padding: "5px 7px",
                    textAlign: "center",
                    background: heat(p, pMax),
                    border: top
                      ? "1px solid var(--se-amber, #d4a017)"
                      : "1px solid var(--se-border, #2a2a2a)",
                    color: "var(--se-text, #fff)",
                  }}
                >
                  {(p * 100).toFixed(1)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
